<script setup lang="ts">
  import { ref, onMounted, onUnmounted } from "vue";
  import { useI18n } from "vue-i18n";
  import Qrcode from "qrcode.vue";
  import { useNotification, Button, Text, IconRfid } from "@ui-kit";
  import { useReadRfid, useWriteRfid, PASSWORD_FOR_WRITE_EPC, LOCK_MEM_TYPE } from "@plugins";
  import { ProductShortCard, StatusBar } from "@src/components";
  import { WTFIRInputType, WTFIRTypeData, useGetWTFIR, useGetAction } from "@src/api";

  const emit = defineEmits<{ (e: "close"): void }>();
  const { epc = null } = defineProps<{ epc: string | null }>();

  const readRfid = useReadRfid();
  const writeRfid = useWriteRfid();
  const { t } = useI18n();
  const { show } = useNotification();
  const { execute: getWTFIR } = useGetWTFIR();
  const { execute: getAction } = useGetAction();

  const productId = ref<string | null>(null);
  const qrCode = ref<string | null>(null);
  const readedEpc = ref<string | null>(null);

  const handleScanRfid = async (data: string) => {
    try {
      if (!data) {
        throw { title: t("EPC метка не найдена"), type: "error" };
      }

      const resultWTFIR = await getWTFIR({ data, input_type: WTFIRInputType.rfid });

      if (resultWTFIR.isError.value) {
        throw { title: t("Ошибка, попробуйте ещё раз"), type: "error" };
      }

      if (data !== epc) {
        if (resultWTFIR.data.value.type_data !== WTFIRTypeData.epcClear) {
          console.log("Прочитали не ту EPC");
          readedEpc.value = data;
          console.log("readedEpc:", readedEpc.value);
          throw { title: t("Приложите чистую EPC метку"), type: "warning" };
        }

        const resultWriteRfidData = await writeRfid.writeData({ epc, filterData: data });

        if (!resultWriteRfidData) {
          throw { title: t("Ошибка записи EPC"), type: "error" };
        }

        const resultWriteRfidPassword = await writeRfid.writePassword({
          newPassword: PASSWORD_FOR_WRITE_EPC,
          filterData: epc,
        });

        if (!resultWriteRfidPassword) {
          throw { title: t("Ошибка записи буфера"), type: "error" };
        }

        const resultLockMem = await writeRfid.lockMem({
          password: PASSWORD_FOR_WRITE_EPC,
          filterData: epc,
          type: LOCK_MEM_TYPE.ALL,
        });

        if (!resultLockMem) {
          throw { title: t("Ошибка блокировки памяти"), type: "error" };
        }

        show({ title: t("EPC метка успешно записана"), type: "success" });
      }

      const resultGetAction = await getAction({ action: "written_epc", data: { epc } });

      if (resultGetAction.isError.value) {
        throw { title: t("Ошибка, попробуйте ещё раз"), type: "error" };
      }

      productId.value = null;
      qrCode.value = resultGetAction.data.value.action;

      readRfid.unsubscribe();
    } catch (e) {
      show({ title: e.title, type: e.type });
    }
  };

  const handleGoToHome = () => {
    productId.value = null;
    qrCode.value = null;

    emit("close");
  };

  const handleResponseEpc = async () => {
    console.log("handleResponceEpc сработал");
    try {
      const resultWTFIR_EPC = await getWTFIR({ data: epc, input_type: WTFIRInputType.rfid });

      if (resultWTFIR_EPC?.isError.value || resultWTFIR_EPC.data.value.type_data !== WTFIRTypeData.epc) {
        throw { title: t("Ошибка, попробуйте ещё раз"), type: "error" };
      }

      if (!resultWTFIR_EPC.data.value.items?.at(0)) {
        throw { title: t("Товар не найден"), type: "error" };
      }

      productId.value = resultWTFIR_EPC.data.value.items?.at(0);

      readRfid.subscribe((data) => handleScanRfid(data.value));
    } catch (e) {
      show({ title: e.title, type: e.type });
      handleGoToHome();
    }
  };

  onMounted(() => {
    if (!epc) {
      show({ title: t("Ошибка при сканировании"), type: "error" });
      handleGoToHome();
    }

    handleResponseEpc();
  });

  onUnmounted(readRfid.unsubscribe);
</script>

<template>
  <div class="widget__label-printing-ut" data-testid="widget-label-printing-ut">
    <div
      v-if="productId && !readedEpc"
      class="label-printing-ut__write-epc"
      data-testid="label-printing-ut-wait-epc-clear"
    >
      <ProductShortCard :productId="productId" @click:back="handleGoToHome" />
      <div class="write-epc__footer">
        <IconRfid />
        <div class="write-epc__footer-description">
          <Text font="body-bold-18">{{ $t("Приложите чистую EPC метку") }}</Text>
          <Text font="body-regular-14" color="secondary">{{ $t("Для создания и печати этикетки") }}</Text>
        </div>
        <Button variant="secondary" @click="handleGoToHome" data-testid="label-printing-ut-btn-cancel">{{
          $t("Отмена")
        }}</Button>
      </div>
    </div>
    <div
      v-if="productId && readedEpc && epc !== readedEpc"
      class="label-printing-ut__write-epc"
      data-testid="label-printing-ut-wrong-epc"
    >
      <ProductShortCard :productId="productId" @click:back="handleGoToHome" />
      <div class="write-epc__footer">
        <IconRfid />
        <div class="write-epc__footer-description">
          <Text font="body-bold-18">{{ $t("Отсканируйте чистую EPC метку") }}</Text>
          <Text font="body-regular-14" color="secondary">{{ $t("Для создания и печати этикетки") }}</Text>
        </div>
        <Button variant="secondary" @click="handleGoToHome" data-testid="label-printing-ut-btn-cancel">{{
          $t("Отмена")
        }}</Button>
      </div>
    </div>
    <div v-if="qrCode" class="label-printing-ut__qr-code" data-testid="label-printing-ut-qr-code">
      <StatusBar style="width: 100%" />
      <div class="qr-code__content">
        <Qrcode :value="qrCode" :size="160" data-testid="label-printing-ut-qr-code-qr" />
        <div class="qr-code__content-description">
          <Text font="heading-H4">{{ $t("RFID метка закодирована") }}</Text>
          <Text color="secondary">{{ $t("Отсканируйте QR с компьютера для печати этикетки") }}</Text>
        </div>
      </div>
      <div class="qr-code__footer">
        <Button @click="handleGoToHome" data-testid="label-printing-success-ut-btn">{{ $t("На главную") }}</Button>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .widget__label-printing-ut {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    z-index: 1;
  }

  .label-printing-ut__write-epc {
    display: flex;
    flex-direction: column;
    flex: 1;
    background-color: var(--sf-base-background-secondary);
  }

  .write-epc__footer {
    display: flex;
    flex-direction: column;
    align-items: center;
    background-color: var(--sf-base-background-white);
    box-shadow: var(--sf-shadow-top-s);
    border-top-left-radius: 16px;
    border-top-right-radius: 16px;
    padding: 20px 16px;
    margin-top: auto;
  }

  .write-epc__footer-description {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-top: 16px;
    text-align: center;
  }

  .write-epc__footer :deep(.button) {
    width: 100%;
    margin-top: 38px;
  }

  .label-printing-ut__qr-code {
    display: flex;
    flex-direction: column;
    flex: 1;
    justify-content: center;
    align-items: center;
    background-color: var(--sf-base-background-white);
  }

  .qr-code__content {
    display: flex;
    flex: 1;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 24px;
    padding: 0 16px;
  }

  .qr-code__content-description {
    display: flex;
    flex-direction: column;
    gap: 8px;
    text-align: center;
  }

  .qr-code__footer {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 16px 16px 20px;
    margin-top: auto;
    width: 100%;
  }

  .qr-code__footer :deep(.button) {
    width: 100%;
  }
</style>
